'use client';

import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import ReactMarkdown from 'react-markdown';
import {
  legalDocumentsService,
  type LegalDocumentAttributes,
} from '../api/legal-documents.service';
import { Loader2 } from 'lucide-react';

interface LegalDocumentPageProps {
  type: 'privacy' | 'terms';
}

export function LegalDocumentPage({ type }: LegalDocumentPageProps) {
  const locale = useLocale();
  const t = useTranslations('account');
  const [document, setDocument] = useState<LegalDocumentAttributes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchDocument() {
      setLoading(true);
      setError(null);
      try {
        const attributes = await legalDocumentsService.get(type, locale);
        if (!cancelled) {
          setDocument(attributes);
        }
      } catch {
        if (!cancelled) {
          setError(t('legal.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchDocument();
    return () => { cancelled = true; };
  }, [type, locale, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-form-help text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-[720px] mx-auto py-2 px-4 sm:px-6 lg:px-8">
      <p className="text-muted-foreground mt-1 mb-6 text-card-meta hidden md:block">
        {document.last_updated}
      </p>

      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown>{document.content}</ReactMarkdown>
      </div>
    </div>
  );
}
